// user-profile.js - Handles user profile display and management

import { apiUrl } from './api-settings.js';
import { showNotification } from './utils.js';

/**
 * Set up user profile in settings
 */
function setupUserProfileSettings() {
  // Add profile section to settings
  const settingsContainer = document.querySelector('.settings-container');
  if (!settingsContainer) return;
  
  const profileSection = document.createElement('div');
  profileSection.className = 'settings-section';
  profileSection.innerHTML = `
    <h3>User Profile</h3>
    <div id="userProfileInfo" class="user-profile-info">
      <div class="profile-loading">Loading user profile...</div>
    </div>
  `;
  
  settingsContainer.appendChild(profileSection);
  
  // Load profile data
  loadUserProfile();
}

/**
 * Load user profile data from API
 */
async function loadUserProfile() {
  const profileInfoElement = document.getElementById('userProfileInfo');
  if (!profileInfoElement) return;
  
  try {
    const authToken = localStorage.getItem("quantumAuthToken");
    if (!authToken) {
      profileInfoElement.innerHTML = '<div class="profile-error">Not authenticated</div>';
      return;
    }
    
    const response = await fetch(`${apiUrl}/user/me`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    });
    
    if (!response.ok) {
      throw new Error("Failed to fetch user profile");
    }
    
    const userData = await response.json();
    
    // Determine account type (Google or Wallet)
    const accountType = userData.googleEmail ? 'Google' : 'Wallet';
    const accountIcon = userData.googleEmail 
      ? `<img src="${userData.googlePicture || 'https://img.icons8.com/color/96/000000/google-logo.png'}" alt="Google" style="width: 24px; height: 24px; border-radius: 50%;">`
      : `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <rect x="2" y="5" width="20" height="14" rx="2" />
          <path d="M16 10a2 2 0 1 0 0 4 2 2 0 0 0 0-4z" />
          <path d="M6 10h4" />
          <path d="M6 14h4" />
        </svg>`;
    
    // Format registration date
    const createdAt = new Date(userData.createdAt).toLocaleDateString();
    
    // Display profile information
    profileInfoElement.innerHTML = `
      <div class="profile-item">
        <div class="profile-label">Account Type</div>
        <div class="profile-value">${accountIcon} ${accountType}</div>
      </div>
      ${userData.googleName ? `
        <div class="profile-item">
          <div class="profile-label">Name</div>
          <div class="profile-value">${userData.googleName}</div>
        </div>
      ` : ''}
      ${userData.googleEmail ? `
        <div class="profile-item">
          <div class="profile-label">Email</div>
          <div class="profile-value">${userData.googleEmail}</div>
        </div>
      ` : ''}
      ${userData.walletAddress && !userData.walletAddress.startsWith('google_') ? `
        <div class="profile-item">
          <div class="profile-label">Wallet Address</div>
          <div class="profile-value" style="font-size: 0.8em; word-break: break-all;">${userData.walletAddress}</div>
        </div>
      ` : ''}
      <div class="profile-item">
        <div class="profile-label">Member Since</div>
        <div class="profile-value">${createdAt}</div>
      </div>
      <div class="profile-item">
        <div class="profile-label">Total Messages</div>
        <div class="profile-value">${userData.messageCount}</div>
      </div>
    `;
    
    // Add styles
    addProfileStyles();
    
  } catch (error) {
    console.error("Error loading user profile:", error);
    profileInfoElement.innerHTML = `<div class="profile-error">Error loading profile: ${error.message}</div>`;
  }
}

/**
 * Add styles for profile display
 */
function addProfileStyles() {
  const styleId = 'user-profile-styles';
  if (document.getElementById(styleId)) return;
  
  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    .user-profile-info {
      padding: 10px;
      background-color: var(--bg-secondary);
      border-radius: 4px;
    }
    .profile-item {
      margin-bottom: 12px;
      display: flex;
      flex-direction: column;
    }
    .profile-label {
      font-size: 0.8em;
      color: var(--text-secondary);
      margin-bottom: 4px;
    }
    .profile-value {
      font-weight: 500;
    }
    .profile-loading, .profile-error {
      padding: 10px;
      color: var(--text-secondary);
      font-style: italic;
    }
    .profile-error {
      color: var(--error);
    }
    @media (min-width: 768px) {
      .profile-item {
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
      }
      .profile-label {
        margin-bottom: 0;
      }
    }
  `;
  document.head.appendChild(style);
}

export { setupUserProfileSettings, loadUserProfile }; 